import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { CustomTheme } from "../../../../types/customTheme";
import CardCommon from "../../../../components/card/CardCommon";
import { useTheme } from "@material-ui/core/styles";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";

export interface License {
  licenseCode: string;
  name: string;
  deviceType: string;
  deviceId: string;
  status: string;
  subscriptionPeriod: string;
  subscriptionAmount: string;
  subscriptionId?: string;
}

interface LicenseTableProps {
  licenses: License[];
  onRowClick: (license: License) => void;
  onChargebeeLink?: (license: License) => void;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    tableContainer: {
      backgroundColor: theme.palette.background.default,
      boxShadow: "none",
    },
    tableRow: {
      "& td": {
        borderBottom: "none",
        padding: "2px 4px",
        fontSize: "12px",
        color: theme.palette.text.primary,
        border: "none",
      },
    },
    cardWrapperCell: {
      padding: 4,
      borderBottom: "none",
      border: "none",
    },
    table: {
      backgroundColor: "transparent",
      borderCollapse: "separate",
      borderSpacing: 0,
      border: "none",
    },
    statusPin: {
      borderRadius: 8,
      padding: "2px 8px",
      fontSize: "12px",
      textAlign: "center",
      fontWeight: 600,
      minWidth: 70,
      display: "inline-block",
    },
    chargebeeIcon: {
      color: theme.palette.custom.green.main,
      fontSize: "20px",
    },
    chargebeeIconWrapper: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      padding: 4,
      borderRadius: 8,
    },
    chargebeeButton: {
      textTransform: "none",
      fontSize: "12px",
      padding: "4px 8px",
      minWidth: "60px",
    },
  })
);

const LicenseTable: React.FC<LicenseTableProps> = ({
  licenses,
  onRowClick,
  onChargebeeLink,
}) => {
  const classes = useStyles();
  const theme = useTheme<CustomTheme>();

  const getStatusStyles = (status: string) => {
    const normalized = status.toLowerCase();

    const statusColorMap: Record<string, { bg: string; text: string }> = {
      active: {
        bg: theme.palette.custom.green.main || "#4CAF50",
        text: theme.palette.custom.green.contrastText || "#fff",
      },
      locked: {
        bg: theme.palette.custom.red.main || "#f44336",
        text: theme.palette.custom.red.contrastText || "#fff",
      },
      trial: {
        bg: theme.palette.custom.blue.main || "#2196f3",
        text: theme.palette.custom.blue.contrastText || "#fff",
      },
      changing: {
        bg: theme.palette.custom.yellow.main || "#FFEB3B",
        text: theme.palette.custom.yellow.contrastText || "#000",
      },
    };

    const fallback = {
      bg: "#9e9e9e",
      text: "#fff",
    };

    const { bg, text } = statusColorMap[normalized] || fallback;

    return {
      backgroundColor: bg,
      color: text,
    };
  };

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    license: License
  ) => {
    const target = event.target as HTMLElement;

    if (target.closest('[data-no-license-edit="true"]')) {
      return;
    }

    onRowClick(license);
  };

  const handleChargebeeClick = (
    event: React.MouseEvent<HTMLElement>,
    license: License
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (onChargebeeLink) {
      onChargebeeLink(license);
    }
  };

  return (
    <CardCommon
      backgroundColor="entity_background"
      style={{ marginTop: 16, border: "none", boxShadow: "none" }}
    >
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={7} className={classes.cardWrapperCell}>
                <CardCommon
                  backgroundColor="table_header_background"
                  style={{ borderRadius: 10, marginBottom: 0 }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    px={1.5}
                    height="42px"
                    alignItems="center"
                  >
                    <Box width="14%">
                      <strong>Name</strong>
                    </Box>

                    <Box width="14%">
                      <strong>License Code</strong>
                    </Box>

                    <Box width="14%">
                      <strong>Device Type</strong>
                    </Box>

                    <Box width="10%">
                      <strong>Subscription</strong>
                    </Box>

                    <Box width="10%">
                      <strong>Chargebee</strong>
                    </Box>

                    <Box
                      width="10%"
                      style={{ textAlign: "right", paddingRight: 69 }}
                    >
                      <strong>Status</strong>
                    </Box>
                  </Box>
                </CardCommon>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {licenses.map((license) => (
              <TableRow
                key={license.licenseCode}
                className={classes.tableRow}
                onClick={(event) => handleRowClick(event, license)}
                style={{ cursor: "pointer" }}
              >
                <TableCell colSpan={7} className={classes.cardWrapperCell}>
                  <CardCommon
                    backgroundColor="entity_background"
                    style={{ borderRadius: 10, marginBottom: 1 }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      px={1.5}
                      height="42px"
                      alignItems="center"
                    >
                      <Box width="14%">{license.name}</Box>

                      <Box width="14%">{license.licenseCode}</Box>

                      <Box width="14%">{license.deviceType}</Box>

                      <Box width="10%">{license.subscriptionPeriod}</Box>

                      <Box
                        width="10%"
                        display="flex"
                        justifyContent="center"
                        data-no-license-edit="true"
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        {license.subscriptionId ? (
                          <span
                            data-no-license-edit="true"
                            className={classes.chargebeeIconWrapper}
                            onClick={(event) =>
                              handleChargebeeClick(event, license)
                            }
                            onMouseDown={(event) => event.stopPropagation()}
                            title="View Chargebee Subscription"
                          >
                            <CheckCircleIcon
                              className={classes.chargebeeIcon}
                            />
                          </span>
                        ) : (
                          <span
                            data-no-license-edit="true"
                            onClick={(event) =>
                              handleChargebeeClick(event, license)
                            }
                            onMouseDown={(event) => event.stopPropagation()}
                            style={{ display: "inline-block" }}
                          >
                           <ButtonCommon
  variant="contained"
  color="green"
  onClick={() => {}}
  className={classes.chargebeeButton}
  style={{
    minWidth: "auto",
    padding: "4px 8px",
  }}
>
  Link
</ButtonCommon>
                          </span>
                        )}
                      </Box>

                      <Box width="10%">
                        <span
                          className={classes.statusPin}
                          style={getStatusStyles(license.status)}
                        >
                          {license.status.charAt(0).toUpperCase() +
                            license.status.slice(1)}
                        </span>
                      </Box>
                    </Box>
                  </CardCommon>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </CardCommon>
  );
};

export default LicenseTable;